/*
  Đo thời gian ở mỗi màn + thao tác UI của staff → HAE (human_activity_events).

  Port từ vucar-workspace/web/src/track.js (cùng ngữ nghĩa), dựng lại cho e2e:

  1. ĐẾM GIỜ NGỒI THẬT. visibleMs chỉ chạy khi tab HIỆN và có tương tác trong IDLE_MS (2').
     durationMs = wall-clock của đoạn (trần MAX_WALL_MS). visibleMs không bao giờ vượt durationMs.
  2. CHỐT TỪNG ĐOẠN: màn mở đủ CHECKPOINT_MS (5') và có giờ ngồi thật thì cắt thành một sự kiện
     rồi đếm tiếp (metadata.cont=true). Tab ẩn thì cắt ngay. Lượt mở màn = số sự kiện cont=false.
  3. LÕI THUẦN: createTracker nhận now/send từ ngoài, test không cần DOM. Phần dính window/document
     nằm ở browser.ts.
  4. ui(name): thao tác không gọi server. Chỉ TÊN thao tác, KHÔNG BAO GIỜ chữ người dùng gõ.
  5. Không PII: chỉ tên màn (route template, không phải đường dẫn cụ thể), tên thao tác, uuid lead/car.
  Hỏng thì im lặng — đo đạc không được làm hỏng việc của staff.

  Khác bản workspace: chỉ MỘT kênh (e2e không có "rail"); mỗi đoạn là một event HAE hoàn chỉnh
  (clientEventId duy nhất, occurredAt = lúc BẮT ĐẦU đoạn, UTC ISO) — backend e2e chỉ việc chuyển tiếp.
*/

export const IDLE_MS = 120_000
export const MIN_WALL_MS = 1000
export const MAX_WALL_MS = 4 * 60 * 60 * 1000
export const FLUSH_EVERY_MS = 60_000
export const FLUSH_AT = 5
export const CHECKPOINT_MS = 5 * 60 * 1000
export const MAX_QUEUE = 100
// Backend nhận tối đa 200/batch; client gửi nhỏ hơn nhiều.
export const MAX_BATCH = 50
// chữ thường, số và / _ : . - (dấu chấm cho hostname link ngoài)
export const UI_NAME_OK = /^[a-z0-9][a-z0-9/_:.-]{0,59}$/

export interface ViewContext {
  /** Route template, vd "/e2e/$userId/lead/$leadId" — KHÔNG phải đường dẫn cụ thể. */
  route?: string | null
  leadId?: string | null
  carId?: string | null
  /** Admin đang xem PIC khác (e2e/$userId/*, watcher/$picId/*...). */
  onBehalfOfId?: string | null
  mobile?: boolean
}

export interface TrackEvent {
  clientEventId: string
  leadId?: string
  carId?: string
  source: "e2e"
  surface: "e2e"
  eventFamily: "screen_view" | "ui_interaction"
  eventName: string
  sessionId: string
  route?: string
  durationMs?: number
  visibleMs?: number
  metadata?: Record<string, unknown>
  occurredAt: string
  onBehalfOfId?: string
}

export interface TrackerOptions {
  now?: () => number
  send?: (events: TrackEvent[], opts?: { beacon?: boolean }) => void
  sessionId?: string
  flushAt?: number
  idleMs?: number
  startRunning?: boolean
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const asUuid = (v: unknown): string | undefined => (typeof v === "string" && UUID_RE.test(v) ? v : undefined)
export const isUuid = (v: unknown): v is string => asUuid(v) !== undefined

interface OpenView {
  view: string
  ctx: ViewContext
  t0: number
  acc: number
  cont: boolean
}

export function createTracker({
  now = () => Date.now(),
  send = () => {},
  sessionId = "s",
  flushAt = FLUSH_AT,
  startRunning = true,
}: TrackerOptions = {}) {
  let cur: OpenView | null = null
  const queue: TrackEvent[] = []
  let running = !!startRunning
  let lastTick = now()
  let seq = 0

  const nextId = () => `${sessionId}:${++seq}`

  function tick() {
    const t = now()
    if (running && cur) {
      const d = t - lastTick
      if (d > 0) cur.acc += d
    }
    lastTick = t
  }

  function push(ev: TrackEvent) {
    queue.push(ev)
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE) // đầy → bỏ dòng CŨ nhất
  }

  function ids(ctx: ViewContext) {
    const out: Pick<TrackEvent, "leadId" | "carId" | "onBehalfOfId"> = {}
    const lead = asUuid(ctx.leadId)
    const car = asUuid(ctx.carId)
    const obo = asUuid(ctx.onBehalfOfId)
    if (lead) out.leadId = lead
    if (car) out.carId = car
    if (obo) out.onBehalfOfId = obo
    return out
  }

  // Chốt ĐOẠN đang chạy thành một event. false nếu quá ngắn.
  function emit(r: OpenView): boolean {
    const wall = Math.min(now() - r.t0, MAX_WALL_MS)
    if (wall < MIN_WALL_MS) return false
    const ev: TrackEvent = {
      clientEventId: nextId(),
      ...ids(r.ctx),
      source: "e2e",
      surface: "e2e",
      eventFamily: "screen_view",
      eventName: `view/${r.view.replace(/^\//, "") || "index"}`.slice(0, 100),
      sessionId,
      durationMs: Math.round(wall),
      visibleMs: Math.max(0, Math.min(Math.round(r.acc), Math.round(wall))),
      metadata: { cont: r.cont, mobile: !!r.ctx.mobile },
      occurredAt: new Date(r.t0).toISOString(),
    }
    if (r.ctx.route) ev.route = r.ctx.route
    push(ev)
    if (queue.length >= flushAt) flush()
    return true
  }

  function closeCurrent() {
    const r = cur
    cur = null
    if (r) emit(r)
  }

  function flush(opts?: { beacon?: boolean }): boolean {
    if (!queue.length) return false
    while (queue.length) {
      const batch = queue.splice(0, MAX_BATCH)
      try {
        send(batch, opts)
      } catch {
        /* đo hỏng thì thôi */
      }
    }
    return true
  }

  return {
    /** Đổi màn đang xem. view rỗng/null = rời hẳn. Cùng view + cùng ngữ cảnh = no-op. */
    setView(view: string | null | undefined, ctx: ViewContext = {}) {
      tick()
      if (cur && view && cur.view === view && sameCtx(cur.ctx, ctx)) return
      closeCurrent()
      if (view) cur = { view: String(view), ctx, t0: now(), acc: 0, cont: false }
    },
    /** Cắt đoạn màn đang mở mà không rời màn (cần đã chạy ≥ minWallMs VÀ có giờ ngồi thật). */
    checkpoint({ minWallMs = CHECKPOINT_MS }: { minWallMs?: number } = {}) {
      tick()
      const t = now()
      const r = cur
      if (!r || r.acc <= 0 || t - r.t0 < minWallMs) return
      if (emit(r)) {
        r.t0 = t
        r.acc = 0
        r.cont = true
      }
    },
    pause() {
      if (!running) return
      tick()
      running = false
    },
    resume() {
      if (running) return
      lastTick = now()
      running = true
    },
    closeAll() {
      tick()
      closeCurrent()
    },
    /** Ghi một thao tác không gọi server. Tên sai khuôn → bỏ, không ném. */
    ui(name: unknown): boolean {
      const n = String(name || "").toLowerCase()
      if (!UI_NAME_OK.test(n)) return false
      const ctx = cur?.ctx ?? {}
      const ev: TrackEvent = {
        clientEventId: nextId(),
        ...ids(ctx),
        source: "e2e",
        surface: "e2e",
        eventFamily: "ui_interaction",
        eventName: `ui/${n}`,
        sessionId,
        occurredAt: new Date(now()).toISOString(),
      }
      if (ctx.route) ev.route = ctx.route
      push(ev)
      if (queue.length >= flushAt * 2) flush()
      return true
    },
    flush,
    _pending: () => queue.slice(),
    _open: () => (cur ? { view: cur.view, acc: cur.acc } : null),
    _running: () => running,
  }
}

function sameCtx(a: ViewContext, b: ViewContext) {
  return (
    (a.route ?? null) === (b.route ?? null) &&
    (a.leadId ?? null) === (b.leadId ?? null) &&
    (a.carId ?? null) === (b.carId ?? null) &&
    (a.onBehalfOfId ?? null) === (b.onBehalfOfId ?? null) &&
    !!a.mobile === !!b.mobile
  )
}

/**
 * Link ra ngoài → 'link:<hostname>' (bỏ www.). Cùng host / blob: / tel: / mailto: → null.
 * Chỉ hostname — path/query có thể chứa mã khách, SĐT.
 */
export function externalLinkName(href: unknown, here = ""): string | null {
  try {
    const u = new URL(String(href || ""), "http://invalid.invalid")
    if (!/^https?:$/.test(u.protocol) || u.host === "invalid.invalid" || u.host === here) return null
    return "link:" + u.hostname.toLowerCase().replace(/^www\./, "").slice(0, 50)
  } catch {
    return null
  }
}

/**
 * Từ route template + params → ngữ cảnh. onBehalfOfId khi admin xem PIC khác:
 * param `userId` (e2e/$userId/*) hoặc `picId` (watcher/$picId/*, persona-management/$picId)
 * khác người đang đăng nhập và là uuid.
 */
export function viewContextFromRoute(
  routeId: string,
  params: Record<string, unknown>,
  meId: string | null | undefined,
  mobile: boolean,
): ViewContext {
  const other = [params.userId, params.picId].map(asUuid).find((v) => v && v !== meId)
  return {
    route: routeId,
    leadId: asUuid(params.leadId) ?? null,
    carId: asUuid(params.carId) ?? null,
    onBehalfOfId: other ?? null,
    mobile,
  }
}

/** "/e2e/$userId/" → "/e2e/$userId"; "/" giữ nguyên. */
export function routeTemplate(routeId: string): string {
  const t = routeId.length > 1 ? routeId.replace(/\/+$/, "") : routeId
  return t || "/"
}
